import React from 'react'
import { crmToast } from './crmUi'
import { useCrm } from './useCrm'
import { getDealAdoption, getModuleStatuses, setModuleStatus, type DealAdoption } from './crmApi'

/**
 * Setup section — what WE recorded about each module, next to what the customer is
 * ACTUALLY doing with it (Phase 2.3).
 *
 * The toggles here are intent: a human's note that a module has been switched on. They
 * were also, until now, the only source for "is this module live" — and across the whole
 * customer base they had been set exactly zero times, so every downstream signal built
 * on them was dead.
 *
 * Adoption is now derived from usage instead (GET /crm/deals/:id/adoption). These
 * toggles still matter, but as a record of intent — and the "Actually" column shows
 * where that intent and reality disagree, which is the part worth acting on:
 *
 *   • marked live, no activity  → the rollout stalled. Someone should call them.
 *   • in use, not marked live   → our records are behind the customer.
 */

type SetupState = 'none' | 'in_progress' | 'done'
interface ModuleStatus { moduleKey: string; label: string; setupState: SetupState; live: boolean; updatedAt?: string | null }

const FALLBACK: ModuleStatus[] = [
  { moduleKey: 'quoting', label: 'Quoting', setupState: 'none', live: false },
  { moduleKey: 'accounts', label: 'Accounts', setupState: 'none', live: false },
  { moduleKey: 'workhub', label: 'WorkHub', setupState: 'none', live: false },
  { moduleKey: 'snagging', label: 'Snagging', setupState: 'none', live: false },
  { moduleKey: 'inventory', label: 'Inventory', setupState: 'none', live: false },
  { moduleKey: 'procurement', label: 'Procurement', setupState: 'none', live: false },
]

const SETUP_OPTS: { value: SetupState; label: string; color: string }[] = [
  { value: 'none', label: 'Not started', color: '#94A3B8' },
  { value: 'in_progress', label: 'In progress', color: '#F59E0B' },
  { value: 'done', label: 'Done', color: '#22C55E' },
]
const fmtDate = (s?: string | null) => {
  if (!s) return '—'
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

const EMPTY_ADOPTION: DealAdoption = { dealId: '', hasUsage: false, seeded: false, modules: [], inUse: 0, setUpUnused: 0, dormant: 0, effectiveLive: 0 }

/** What the customer is really doing with this module — and where that contradicts the toggle. */
const ActualCell: React.FC<{ a?: { state: string; current: number; mismatch: string | null }; hasUsage: boolean; updatedAt?: string | null }> = ({ a, hasUsage, updatedAt }) => {
  if (!hasUsage || !a) return <span className="text-[11px] text-txt-tertiary">No usage data · set {fmtDate(updatedAt)}</span>
  if (a.mismatch === 'live_but_unused') {
    return <span className="text-[11px] font-semibold" style={{ color: '#B45309' }}>Marked live — but zero activity. Rollout stalled.</span>
  }
  if (a.mismatch === 'used_but_not_marked') {
    return <span className="text-[11px]" style={{ color: '#0369A1' }}>{a.current} actions — in use, but not marked live here</span>
  }
  if (a.state === 'in_use') return <span className="text-[11px]" style={{ color: '#166534' }}>{a.current} actions this period</span>
  if (a.state === 'dormant') return <span className="text-[11px] font-semibold" style={{ color: '#B45309' }}>Used before — nothing this period</span>
  return <span className="text-[11px] text-txt-tertiary">Never used</span>
}

export const DealSetup: React.FC<{ dealId: string }> = ({ dealId }) => {
  const { data: modules, setData } = useCrm<ModuleStatus[]>(() => getModuleStatuses(dealId) as Promise<ModuleStatus[]>, FALLBACK, [dealId])
  // What the customer actually does — the truth against which the toggles are checked.
  const { data: adoption } = useCrm<DealAdoption>(() => getDealAdoption(dealId) as Promise<DealAdoption>, EMPTY_ADOPTION, [dealId])
  const actual = Object.fromEntries(adoption.modules.map((m) => [m.key, m]))

  const update = (key: string, patch: { setupState?: SetupState; live?: boolean }) => {
    setData((prev) => prev.map((m) => (m.moduleKey === key ? { ...m, ...patch, updatedAt: new Date().toISOString() } : m)))
    setModuleStatus(dealId, key, patch).catch(() => crmToast('Could not save — is the backend live?', 'error'))
  }

  return (
    <div className="max-w-[720px]">
      <p className="text-sm text-txt-secondary mb-3">Track each platform module: has the customer set it up, and is it live for them?</p>
      <div className="rounded-xl border border-border overflow-hidden">
        <div className="grid grid-cols-[1fr_140px_80px_1fr] gap-2 px-4 py-2.5 border-b border-border bg-surface-secondary text-[11px] font-semibold uppercase tracking-wide text-txt-tertiary">
          <span>Module</span><span>Setup</span><span>Live</span><span>Actually</span>
        </div>
        {modules.map((m) => (
          <div key={m.moduleKey} className="grid grid-cols-[1fr_140px_80px_1fr] gap-2 items-center px-4 py-3 border-b border-border last:border-0">
            <span className="text-sm font-medium text-txt-primary">{m.label}</span>
            <select
              value={m.setupState}
              onChange={(e) => update(m.moduleKey, { setupState: e.target.value as SetupState })}
              className="px-2 py-1.5 rounded-lg bg-surface-secondary border border-border text-xs font-medium outline-none focus:border-brand-500"
              style={{ color: SETUP_OPTS.find((o) => o.value === m.setupState)?.color }}
            >
              {SETUP_OPTS.map((o) => <option key={o.value} value={o.value} style={{ color: 'inherit' }}>{o.label}</option>)}
            </select>
            <button
              onClick={() => update(m.moduleKey, { live: !m.live })}
              className={`relative w-11 h-6 rounded-full transition-colors ${m.live ? 'bg-green-500' : 'bg-surface-tertiary'}`}
              title={m.live ? 'Live for this customer' : 'Not live'}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${m.live ? 'left-[22px]' : 'left-0.5'}`} />
            </button>
            <ActualCell a={actual[m.moduleKey]} hasUsage={adoption.hasUsage} updatedAt={m.updatedAt} />
          </div>
        ))}
      </div>
      <p className="text-[11px] text-txt-tertiary mt-2">
        “Live” is what we recorded. <span className="font-semibold">“Actually” is what the customer does</span> — derived from
        their WeQuote activity, so it is right whether or not anyone kept these toggles up to date.
        {adoption.seeded && <span> (Activity shown is seeded demo data.)</span>}
      </p>
    </div>
  )
}
